import { useState, useEffect } from 'react';
import { CalendarClock, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { API_BASE } from '../../utils/api-base';
import { sessionStorage as authSessionStorage } from '../../utils/auth-api';

function getToken() {
  return authSessionStorage.get()?.token || '';
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const defaultSchedule = () => ({
  name: '',
  report_type: 'all',
  format: 'csv',
  frequency: 'daily',
  hour: 8,
  day_of_week: 1,
  day_of_month: 1,
  date_range_days: 1,
  enabled: true,
});

export function OneDriveScheduleDialog({ clientId, clientName, open, onClose }: {
  clientId: string;
  clientName: string;
  open: boolean;
  onClose: () => void;
}) {
  const [schedules, setSchedules] = useState<any[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // New/Edit schedule dialog
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<any | null>(null);
  const [scheduleForm, setScheduleForm] = useState(defaultSchedule());
  const [savingSchedule, setSavingSchedule] = useState(false);

  const fetchSchedules = async () => {
    setSchedulesLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/admin/clients/${clientId}/report-schedules`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setSchedules(await resp.json());
    } catch {
      setSchedules([]);
    } finally {
      setSchedulesLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchSchedules();
  }, [open, clientId]);

  const handleDeleteSchedule = async (id: string) => {
    if (!confirm('Delete this schedule?')) return;
    setDeletingId(id);
    try {
      await fetch(`${API_BASE}/api/admin/report-schedules/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      toast.success('Schedule deleted');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const openAddSchedule = () => {
    setEditingSchedule(null);
    setScheduleForm(defaultSchedule());
    setScheduleDialog(true);
  };

  const openEditSchedule = (s: any) => {
    setEditingSchedule(s);
    setScheduleForm({
      name: s.name,
      report_type: s.report_type,
      format: s.format,
      frequency: s.frequency,
      hour: s.hour,
      day_of_week: s.day_of_week ?? 1,
      day_of_month: s.day_of_month ?? 1,
      date_range_days: s.date_range_days,
      enabled: s.enabled,
    });
    setScheduleDialog(true);
  };

  const handleSaveSchedule = async () => {
    if (!scheduleForm.name.trim()) { toast.error('Schedule name required'); return; }
    setSavingSchedule(true);
    try {
      const url = editingSchedule
        ? `${API_BASE}/api/admin/report-schedules/${editingSchedule.id}`
        : `${API_BASE}/api/admin/clients/${clientId}/report-schedules`;
      const method = editingSchedule ? 'PUT' : 'POST';
      const resp = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleForm),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error);
      toast.success(editingSchedule ? 'Schedule updated' : 'Schedule created');
      setScheduleDialog(false);
      fetchSchedules();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleEnabled = async (s: any) => {
    try {
      await fetch(`${API_BASE}/api/admin/report-schedules/${s.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...s, enabled: !s.enabled }),
      });
      setSchedules((prev) => prev.map((r) => r.id === s.id ? { ...r, enabled: !r.enabled } : r));
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const formatNextRun = (iso: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  const formatLabel = (fmt: string) => {
    if (fmt === 'excel') return 'Excel';
    if (fmt === 'pdf') return 'PDF';
    return 'CSV';
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-blue-500" />
              Auto-Report Schedules — {clientName}
            </DialogTitle>
          </DialogHeader>

          <p className="text-xs text-muted-foreground">
            Schedules run automatically in the client's browser when they log in. Reports are saved to the folder the client configures in their Settings screen.
          </p>

          {/* Schedules */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Schedules</p>
              <Button size="sm" onClick={openAddSchedule}>
                <Plus className="h-3 w-3 mr-1" /> Add Schedule
              </Button>
            </div>

            {schedulesLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : schedules.length === 0 ? (
              <p className="text-xs text-muted-foreground">No schedules yet. Add one to get started.</p>
            ) : (
              <div className="space-y-2">
                {schedules.map((s) => (
                  <div key={s.id} className="border rounded-lg p-3 text-sm space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Switch checked={s.enabled} onCheckedChange={() => toggleEnabled(s)} />
                        <span className="font-medium truncate">{s.name}</span>
                        <Badge variant="outline" className="text-xs shrink-0">{s.report_type}</Badge>
                        <Badge variant="secondary" className="text-xs shrink-0">{formatLabel(s.format)}</Badge>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditSchedule(s)}>
                          <span className="text-xs">✎</span>
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDeleteSchedule(s.id)} disabled={deletingId === s.id}>
                          {deletingId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                        </Button>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground pl-8">
                      {s.frequency === 'daily' && `Daily at ${String(s.hour).padStart(2,'0')}:00`}
                      {s.frequency === 'weekly' && `Every ${DAYS_OF_WEEK[s.day_of_week ?? 1]} at ${String(s.hour).padStart(2,'0')}:00`}
                      {s.frequency === 'monthly' && `Monthly on day ${s.day_of_month ?? 1} at ${String(s.hour).padStart(2,'0')}:00`}
                      {' · '}{s.date_range_days} day{s.date_range_days !== 1 ? 's' : ''} of data
                      {s.last_run_at && <> · Last: {new Date(s.last_run_at).toLocaleDateString()}</>}
                      {' · '}Next: {formatNextRun(s.next_run_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit schedule dialog */}
      <Dialog open={scheduleDialog} onOpenChange={(v) => !v && setScheduleDialog(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSchedule ? 'Edit Schedule' : 'New Report Schedule'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Schedule Name</Label>
              <Input value={scheduleForm.name} onChange={(e) => setScheduleForm((f: any) => ({ ...f, name: e.target.value }))} placeholder="e.g. Daily PLC Report" className="mt-1" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Report Type</Label>
                <Select value={scheduleForm.report_type} onValueChange={(v) => setScheduleForm((f: any) => ({ ...f, report_type: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All (PLC + Energy)</SelectItem>
                    <SelectItem value="plc">PLC Only</SelectItem>
                    <SelectItem value="energy_meter">Energy Meter Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Format</Label>
                <Select value={scheduleForm.format} onValueChange={(v) => setScheduleForm((f: any) => ({ ...f, format: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Frequency</Label>
                <Select value={scheduleForm.frequency} onValueChange={(v) => setScheduleForm((f: any) => ({ ...f, frequency: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Hour (0–23 UTC)</Label>
                <Input type="number" min={0} max={23} value={scheduleForm.hour} onChange={(e) => setScheduleForm((f: any) => ({ ...f, hour: parseInt(e.target.value) || 0 }))} className="mt-1" />
              </div>
            </div>

            {scheduleForm.frequency === 'weekly' && (
              <div>
                <Label className="text-xs">Day of Week</Label>
                <Select value={String(scheduleForm.day_of_week)} onValueChange={(v) => setScheduleForm((f: any) => ({ ...f, day_of_week: parseInt(v) }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {scheduleForm.frequency === 'monthly' && (
              <div>
                <Label className="text-xs">Day of Month (1–28)</Label>
                <Input type="number" min={1} max={28} value={scheduleForm.day_of_month} onChange={(e) => setScheduleForm((f: any) => ({ ...f, day_of_month: parseInt(e.target.value) || 1 }))} className="mt-1" />
              </div>
            )}

            <div>
              <Label className="text-xs">Data Range (days to include)</Label>
              <Input type="number" min={1} max={90} value={scheduleForm.date_range_days} onChange={(e) => setScheduleForm((f: any) => ({ ...f, date_range_days: parseInt(e.target.value) || 1 }))} className="mt-1" />
              <p className="text-xs text-muted-foreground mt-1">e.g. 1 = yesterday's data, 7 = last 7 days</p>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={scheduleForm.enabled} onCheckedChange={(v) => setScheduleForm((f: any) => ({ ...f, enabled: v }))} />
              <Label className="text-xs">Enabled</Label>
            </div>

            <Button className="w-full" onClick={handleSaveSchedule} disabled={savingSchedule}>
              {savingSchedule ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
